import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle } from 'lucide-react';
import apiService from '../../services/apiService';
import { useI18n } from '../../utils/i18n';
import { formatDate } from '../../utils/ticketFormat';
import TicketCard from '../ui/TicketCard';
import ListState from '../ui/ListState';

// #650: Dashboard-Bereich „Zuletzt abgeschlossen“, damit ein versehentlich
// abgeschlossenes Ticket ohne Suche wiederzufinden ist (Beschluss C.Firchau
// 03.11.2026: 14 Tage, höchstens 50, „von mir“ | „alle“, kein Rückgängig-Toast).
// Von sich aus ausgeblendet (`standardAus` in bereiche.js).
//
// Muster aus #471: Den Hook ruft das Dashboard auf, die Komponente rendert
// nur. So übersteht die gewählte Sicht das frühe `return` des Dashboards bei
// Laden/Fehler. Geladen wird nur, solange der Bereich sichtbar ist (`aktiv`),
// und neu bei jedem `ticketsUpdated` (Abschluss, Wiederöffnen aus dem Dialog).
// Ein Klick öffnet das Ticket über `openTicketDetailById`; GET /api/tickets/:id
// findet auch Archiv-Tickets, „Wieder öffnen“ gibt es dort schon.

export function useZuletztAbgeschlossen({ aktiv }) {
  const [umfang, setUmfang] = useState('mine');
  const [tickets, setTickets] = useState([]);
  // 'loading' nur bis zum ersten Ergebnis je Umfang, danach bleiben die alten Daten stehen
  const [status, setStatus] = useState('loading');
  const anfrage = useRef(0);

  const lade = useCallback(async (welcher) => {
    const nr = ++anfrage.current;
    try {
      const data = await apiService.getRecentlyClosedTickets(welcher);
      if (nr !== anfrage.current) return; // überholt (Umschalten während des Ladens)
      setTickets(Array.isArray(data && data.tickets) ? data.tickets : []);
      setStatus('ok');
    } catch (_) {
      if (nr !== anfrage.current) return;
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!aktiv) return undefined;
    lade(umfang);
    const neuLaden = () => lade(umfang);
    window.addEventListener('ticketsUpdated', neuLaden);
    return () => window.removeEventListener('ticketsUpdated', neuLaden);
  }, [aktiv, umfang, lade]);

  const waehleUmfang = useCallback((neu) => {
    if (neu === umfang) return;
    setUmfang(neu);
    setTickets([]);
    setStatus('loading');
  }, [umfang]);

  const erneut = useCallback(() => {
    setStatus('loading');
    lade(umfang);
  }, [lade, umfang]);

  return { umfang, waehleUmfang, tickets, status, erneut };
}

// Lokaler Teil der Adresse als kurze Kennung („A.Schindler“)
const kurzName = (mail) => String(mail || '').split('@')[0];

export default function ZuletztAbgeschlossen({ zuletzt }) {
  const { t, getLocale } = useI18n();
  const { umfang, waehleUmfang, tickets, status, erneut } = zuletzt;

  const oeffne = (ticket) => {
    try { window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId: ticket.id } })); } catch (_) {}
  };

  let inhalt;
  if (status === 'loading' && tickets.length === 0) {
    inhalt = <ListState variant="loading" />;
  } else if (status === 'error' && tickets.length === 0) {
    inhalt = <ListState variant="error" onRetry={erneut} />;
  } else if (tickets.length === 0) {
    inhalt = (
      <ListState
        icon={<CheckCircle size={32} style={{ opacity: 0.5 }} aria-hidden="true" />}
        message={umfang === 'mine'
          ? t('dashboard.zuletztLeerMine', 'Keine eigenen Abschlüsse in den letzten 14 Tagen.')
          : t('dashboard.zuletztLeerAll', 'Keine Abschlüsse in den letzten 14 Tagen.')}
      />
    );
  } else {
    inhalt = (
      <div className="dashboard-ticket-list">
        {tickets.map((ticket) => {
          const wann = ticket.resolvedAt ? formatDate(ticket.resolvedAt, getLocale()) : '';
          const zeile = umfang === 'all' && ticket.closedBy
            ? t('dashboard.zuletztAmVon', 'Abgeschlossen {{wann}} von {{wer}}', { wann, wer: kurzName(ticket.closedBy) })
            : t('dashboard.zuletztAm', 'Abgeschlossen {{wann}}', { wann });
          return (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              hasNew={false}
              onClick={() => oeffne(ticket)}
              footerRow={<div className="zuletzt-abgeschlossen__zeile">{zeile}</div>}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="dashboard-section zuletzt-abgeschlossen">
      <div className="section-header">
        <CheckCircle size={20} />
        <h2>{t('dashboard.zuletzt', 'Zuletzt abgeschlossen')}</h2>
        <div className="zuletzt-abgeschlossen__umfang" role="group" aria-label={t('dashboard.zuletztUmfang', 'Welche Tickets')}>
          {[['mine', t('dashboard.zuletztMine', 'Von mir')], ['all', t('dashboard.zuletztAll', 'Alle')]].map(([wert, name]) => (
            <button
              key={wert}
              type="button"
              className={`btn btn-sm ${umfang === wert ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={umfang === wert}
              onClick={() => waehleUmfang(wert)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
      {inhalt}
    </div>
  );
}
